"use client"

import { useState, useEffect, useRef } from "react"
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import {
  ChevronDown,
  FileText,
  Book,
  Calendar,
  LogOut,
  MessageCircle,
  Menu,
  Mail,
  GraduationCap,
  User,
  X,
  Copy,
  Check,
  PenTool,
  TestTube,
  GraduationCapIcon,
} from "lucide-react"
import { getStudentInfo, logoutStudent, getClassData } from "./api"
import { generatePresignedUrl } from "../postFileAws"
import { useDispatch, useSelector } from "react-redux"
import { setSelectedClass, setSelectedSubject, setClassData } from "../slices/classSubjectSlice"
import NotificationBell from "./notificationBell"
// Import the Firebase messaging service with updated functions
import { setupMessageHandler, checkTokenOnServer, cleanupFCMToken } from "../firebase-messaging"

const safeStringify = (obj) => {
  const cache = new Set()
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (cache.has(value)) {
        return "[Circular]"
      }
      cache.add(value)
    }
    return value
  })
}

const safeParse = (str) => {
  try {
    return JSON.parse(str)
  } catch (e) {
    console.error("Error parsing JSON:", e)
    return null
  }
}

const initializeLocalStorage = () => {
  const storedData = {
    class: localStorage.getItem("class"),
    subject: localStorage.getItem("subject"),
    classRoomId: localStorage.getItem("classRoomId"),
  }

  const isValid = Object.values(storedData).every((value) => value !== null && value !== undefined)

  if (isValid) {
    return storedData
  } else {
    const defaultData = {
      class: "",
      subject: "",
      classRoomId: "",
    }
    Object.entries(defaultData).forEach(([key, value]) => localStorage.setItem(key, value))
    return defaultData
  }
}

// Skeleton Loading Component
const SkeletonLoader = () => {
  return (
    <div className="space-y-4 animate-pulse">
      {/* Class Dropdown Skeleton */}
      <div className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center flex-1 min-w-0">
          <GraduationCap size={18} className="mr-3 flex-shrink-0 text-gray-300" />
          <div className="h-4 bg-gray-200 rounded flex-1 max-w-[140px]"></div>
        </div>
        <ChevronDown size={16} className="text-gray-300 ml-2 flex-shrink-0" />
      </div>

      {/* Class Code Skeleton */}
      <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-between">
        <div className="flex-1 flex items-center">
          <div className="h-3 bg-gray-200 rounded w-16 mr-2"></div>
          <div className="h-3 bg-gray-200 rounded w-20"></div>
        </div>
        <div className="w-4 h-4 bg-gray-200 rounded"></div>
      </div>
    </div>
  )
}

export default function Layout() {
  const dispatch = useDispatch()
  const { selectedClass, selectedSubject } = useSelector((state) => state.classSubject)

  const [enrolledClasses, setEnrolledClasses] = useState([])
  const [showClassSubjectDropdown, setShowClassSubjectDropdown] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [studentName, setStudentName] = useState("")
  const [error, setError] = useState(null)
  const [isCopied, setIsCopied] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const [profileImageUrl, setProfileImageUrl] = useState("")
  const [isTopMenuOpen, setIsTopMenuOpen] = useState(false)
  const [classCode, setClassCode] = useState("")
  const [studentId, setStudentId] = useState("")

  const location = useLocation()
  const navigate = useNavigate()
  const dropdownRef = useRef(null)
  const profileDropdownRef = useRef(null)
  const sidebarRef = useRef(null)
  const topMenuRef = useRef(null)

  // Add a ref to store the message handler unsubscribe function
  const messageHandlerUnsubscribeRef = useRef(null)

  const [isMobileView, setIsMobileView] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  // Initialize Firebase Cloud Messaging with updated implementation
  useEffect(() => {
    // Initialize FCM for the application
    const initFcm = async () => {
      try {
        // Import the initialization function
        const { initializeFirebaseMessaging } = await import("../firebase-messaging")

        // Initialize Firebase messaging
        const token = await initializeFirebaseMessaging()
        console.log("Firebase messaging initialized with token:", token)

        // Set up message handler for the Layout component
        const unsubscribe = setupMessageHandler((payload) => {
          console.log("Message received in Layout component:", payload)

          // Display notification even when app is in foreground
          if (Notification.permission === "granted") {
            const { title, body } = payload.notification || {}
            if (title) {
              const notification = new Notification(title, {
                body,
                icon: "/notification-icon.png",
              })

              // Handle notification click
              notification.onclick = () => {
                // Navigate to communication page or specific chat
                if (payload.data && payload.data.gid) {
                  navigate(`/communication?gid=${payload.data.gid}`)
                } else {
                  navigate("/communication")
                }

                // Focus window
                window.focus()

                // Close notification
                notification.close()
              }
            }
          }
        })

        // Store the unsubscribe function
        messageHandlerUnsubscribeRef.current = unsubscribe

        // Check current tokens for this user
        if (studentId) {
          const tokenStatus = await checkTokenOnServer(studentId)
          console.log("Current user token status:", tokenStatus)
        }
      } catch (error) {
        console.error("Error initializing FCM in Layout:", error)
      }
    }

    // Initialize FCM if we have a student ID
    if (studentId) {
      initFcm()
    }

    // Clean up when component unmounts
    return () => {
      // We don't actually call unsubscribe here
      // This ensures the handler persists even when the component unmounts
      console.log("Layout component unmounting, but keeping message handler active")
    }
  }, [navigate, studentId])

  const updateLocalStorageAndRedux = (data) => {
    Object.entries(data).forEach(([key, value]) => {
      localStorage.setItem(key, value)
      switch (key) {
        case "class":
          dispatch(setSelectedClass(value))
          break
        case "subject":
          dispatch(setSelectedSubject(value))
          break
        default:
          break
      }
    })
  }

  useEffect(() => {
    const fetchStudentData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await getStudentInfo()

        if (!response) {
          throw new Error("No response received from API")
        }

        if (!response.body || !response.body.data) {
          throw new Error("No class joined yet")
        }

        const data = response.body.data
        localStorage.setItem("studentData", safeStringify(data))
        setStudentName(data.studentName)
        setStudentId(data._id)
        localStorage.setItem("studentId", data._id)

        if (data.profilePhotoS3Uri) {
          const presignedUrl = await generatePresignedUrl(data.profilePhotoS3Uri)
          setProfileImageUrl(presignedUrl)
        }

        setEnrolledClasses(data.enrolledClasses || [])

        const storedData = initializeLocalStorage()

        if (!storedData.class) {
          if (data.enrolledClasses && data.enrolledClasses.length > 0) {
            const defaultClass = data.enrolledClasses[0]
            updateLocalStorageAndRedux({
              class: defaultClass.classValue,
              subject: defaultClass.subject,
              classRoomId: defaultClass.classRoomId,
            })

            // Set class code if available
            if (defaultClass.classCode) {
              setClassCode(defaultClass.classCode)
            }

            const classResponse = await getClassData(defaultClass.classRoomId)
            if (classResponse && classResponse.body && classResponse.body.data) {
              dispatch(setClassData(classResponse.body.data))
            }
          }
        } else {
          updateLocalStorageAndRedux(storedData)

          // Find class code for the selected class
          const selectedClassData = data.enrolledClasses?.find(
            (c) => c.classValue === storedData.class && c.subject === storedData.subject,
          )

          if (selectedClassData?.classCode) {
            setClassCode(selectedClassData.classCode)
          }

          const classRoomId = localStorage.getItem("classRoomId")
          if (classRoomId) {
            const classResponse = await getClassData(classRoomId)
            if (classResponse && classResponse.body && classResponse.body.data) {
              dispatch(setClassData(classResponse.body.data))
            }
          }
        }

        setError(null)
      } catch (error) {
        console.error("Error fetching student data:", error)
        setError(error.message || "Failed to load student data. Please try again later.")
        if (error.message === "No token found" || error.response?.status === 401) {
          navigate("/login")
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchStudentData()
  }, [dispatch, navigate])

  const toggleClassSubjectDropdown = () => {
    setShowClassSubjectDropdown((prev) => !prev)
  }

  const selectClassAndSubject = async (classData) => {
    const currentClass = localStorage.getItem("class")
    const currentSubject = localStorage.getItem("subject")

    updateLocalStorageAndRedux({
      class: classData.classValue,
      subject: classData.subject,
      classRoomId: classData.classRoomId,
    })

    if (classData.classCode) {
      setClassCode(classData.classCode)
    }

    setShowClassSubjectDropdown(false)

    try {
      const classResponse = await getClassData(classData.classRoomId)
      if (classResponse && classResponse.body && classResponse.body.data) {
        dispatch(setClassData(classResponse.body.data))
      }
    } catch (error) {
      console.error("Error fetching class data:", error)
    }

    if (currentClass !== classData.classValue || currentSubject !== classData.subject) {
      navigate("/dashboard")
      window.location.reload()
    }
  }

  const toggleSidebar = () => {
    if (isMobileView) {
      setIsMobileMenuOpen(!isMobileMenuOpen)
    } else {
      setIsSidebarCollapsed(!isSidebarCollapsed)
    }
  }

  // Updated logout handler for multi-device token management
  const handleLogout = async () => {
    try {
      // Get FCM token and studentId from localStorage
      const fcmToken = localStorage.getItem("fcmToken")
      const studentId = localStorage.getItem("studentId")

      // If we have a token and studentId, remove it from the server
      if (fcmToken && studentId) {
        console.log("Removing FCM token from database during logout")

        // Use the updated cleanupFCMToken function to remove the specific token
        await cleanupFCMToken(studentId, fcmToken)
      }

      // Clear localStorage and log out
      logoutStudent()
      localStorage.clear()
      navigate("/login")
    } catch (error) {
      console.error("Error during logout:", error)
      // Still perform logout even if token removal fails
      logoutStudent()
      localStorage.clear()
      navigate("/login")
    }
  }

  const getCurrentSection = () => {
    const path = location.pathname.split("/")[1]
    switch (path) {
      case "dashboard":
        return "Dashboard"
      case "library":
        return "Library"
      case "notes":
        return "Notes"
      case "assignments":
        return "Assignments"
      case "quiz":
        return "Quiz"
      case "eLearning":
        return "E-Learning"
      case "calendar":
        return "Calendar"
      case "post":
        return "Post"
      case "communication":
        return "Chit-Chat"
      case "settings":
        return "Settings"
      default:
        return "Dashboard"
    }
  }

  const copyClassCode = () => {
    navigator.clipboard.writeText(classCode)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 5000)
  }

  const MenuItem = ({ to, Icon, text }) => {
    const isActive = location.pathname.startsWith(to)
    const isDisabled = enrolledClasses.length === 0 && to !== "/dashboard"

    const handleClick = () => {
      if (isMobileView && isMobileMenuOpen) {
        setIsMobileMenuOpen(false)
      }
    }

    return (
      <li className="mb-4">
        <Link
          to={isDisabled ? "#" : to}
          className={`flex items-center py-3 px-4 rounded-lg transition-all duration-200 ${
            isActive ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-100"
          } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={handleClick}
        >
          <Icon size={22} className={isSidebarCollapsed && !isMobileView ? "mx-auto" : "mr-3"} />
          {(!isSidebarCollapsed || isMobileView) && <span className="text-base font-medium">{text}</span>}
        </Link>
      </li>
    )
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false)
      }
      if (topMenuRef.current && !topMenuRef.current.contains(event.target)) {
        setIsTopMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    function handleClickOutside(event) {
      if (isMobileView && isMobileMenuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isMobileMenuOpen, isMobileView])

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      {/* Left Sidebar */}
      <aside
        ref={sidebarRef}
        className={`bg-white shadow-lg transition-all duration-300 ease-in-out ${
          isMobileView ? (isMobileMenuOpen ? "w-64" : "w-0") : isSidebarCollapsed ? "w-20" : "w-64"
        } flex-shrink-0 flex flex-col fixed md:relative z-20 h-full`}
      >
        <div className="flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              {(!isSidebarCollapsed || isMobileView) && (
                <h1 className="text-2xl font-bold text-gray-800">Student Portal</h1>
              )}
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <X size={24} />
                </button>
              )}
            </div>
            {(!isSidebarCollapsed || isMobileView) && (
              <div className="space-y-4" ref={dropdownRef}>
                {/* Fixed height container to prevent menu dancing */}
                <div className="min-h-[120px]">
                  {isLoading ? (
                    <SkeletonLoader />
                  ) : error ? (
                    <div className="flex items-center justify-center h-[120px]">
                      <p className="text-red-500 text-sm text-center">Error: {error}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <button
                          onClick={toggleClassSubjectDropdown}
                          disabled={enrolledClasses.length === 0}
                          className={`flex items-center justify-between w-full p-3 text-sm font-medium text-left bg-gray-50 rounded-lg transition-colors duration-200 ${
                            enrolledClasses.length === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"
                          }`}
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <GraduationCap size={18} className="mr-3 flex-shrink-0 text-blue-500" />
                            <span className="truncate">
                              {enrolledClasses.length > 0
                                ? `${selectedClass || "Select Class"} - ${selectedSubject || "Select Subject"}`
                                : "No classes enrolled"}
                            </span>
                          </div>
                          <ChevronDown
                            size={16}
                            className={`text-gray-500 transition-transform duration-200 ml-2 flex-shrink-0 ${
                              showClassSubjectDropdown ? "transform rotate-180" : ""
                            }`}
                          />
                        </button>
                        {showClassSubjectDropdown && enrolledClasses.length > 0 && (
                          <ul className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {enrolledClasses.map((classData) => (
                              <li
                                key={`${classData.classValue}-${classData.subject}`}
                                onClick={() => selectClassAndSubject(classData)}
                                className="px-4 py-3 text-sm hover:bg-gray-50 cursor-pointer truncate"
                              >
                                {classData.classValue} - {classData.subject}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {classCode && (
                        <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-between">
                          <div className="flex-1 overflow-hidden">
                            <span className="text-sm font-semibold text-gray-600">Class Code:</span>
                            <span className="ml-2 text-sm text-gray-800">{classCode}</span>
                          </div>
                          <button
                            onClick={copyClassCode}
                            className="p-1 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                            title="Copy class code"
                          >
                            {isCopied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <nav className="mt-8">
            <ul className="space-y-4 px-4">
              <MenuItem to="/dashboard" Icon={User} text="Dashboard" />
              <MenuItem to="/library" Icon={Book} text="Library" />
              <MenuItem to="/notes" Icon={PenTool} text="Notes" />
              <MenuItem to="/assignments" Icon={FileText} text="Assignments" />
              <MenuItem to="/quiz" Icon={TestTube} text="Quiz" />
              <MenuItem to="/elearning" Icon={GraduationCapIcon} text="E-Learning" />
            </ul>
          </nav>
        </div>

        <div className="p-4 border-t border-gray-200">
          {!isSidebarCollapsed && (!isMobileView || isMobileMenuOpen) && (
            <button
              onClick={handleLogout}
              className="flex items-center justify-center w-full p-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors duration-200"
            >
              <LogOut size={18} className="mr-2" />
              <span>Logout</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Navigation Bar */}
        <header className="bg-white shadow-sm py-2 px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Menu size={24} />
              </button>
              <h1 className="text-xl font-bold text-gray-800 ml-4 truncate">{getCurrentSection()}</h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/calendar" className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none">
                <Calendar size={20} />
              </Link>
              <NotificationBell />
              <Link to="/post" className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none">
                <Mail size={20} />
              </Link>
              <Link to="/communication" className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none">
                <MessageCircle size={20} />
              </Link>
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center focus:outline-none"
                >
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl || "/placeholder.svg"}
                      alt="User"
                      className="w-8 h-8 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <User size={18} className="text-gray-500" />
                    </div>
                  )}

                  <span className="text-sm font-medium ml-2">{studentName}</span>
                  <ChevronDown size={16} className="ml-1 text-gray-500" />
                </button>

                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout()
                        setIsProfileDropdownOpen(false)
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center space-x-2 ">
              {/* Notification Bell outside profile dropdown */}
              <div className="mt-3">
                <NotificationBell iconOnly={true} />
              </div>
              <div className="relative" ref={topMenuRef}>
                <button
                  onClick={() => setIsTopMenuOpen(!isTopMenuOpen)}
                  className="p-2 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl || "/placeholder.svg"}
                      alt="User"
                      className="w-8 h-8 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <User size={18} className="text-gray-500" />
                    </div>
                  )}
                </button>

                {isTopMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-800">{studentName}</p>
                    </div>
                    <div className="py-2 grid grid-cols-3 gap-1 px-2 border-b border-gray-100">
                      <Link to="/calendar" className="flex flex-col items-center p-2 rounded hover:bg-gray-100">
                        <Calendar size={20} className="text-gray-600" />
                        <span className="text-xs mt-1">Calendar</span>
                      </Link>
                      <Link to="/post" className="flex flex-col items-center p-2 rounded hover:bg-gray-100">
                        <Mail size={20} className="text-gray-600" />
                        <span className="text-xs mt-1">Posts</span>
                      </Link>
                      <Link to="/communication" className="flex flex-col items-center p-2 rounded hover:bg-gray-100">
                        <MessageCircle size={20} className="text-gray-600" />
                        <span className="text-xs mt-1">Chat</span>
                      </Link>
                    </div>
                    <Link
                      to="/student/profile-edit"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsTopMenuOpen(false)}
                    >
                      Edit Profile
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout()
                        setIsTopMenuOpen(false)
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Dynamic Content Area */}
        <div className="flex-1 overflow-auto bg-gray-50">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
